import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Check, Shield, Sparkles, Users, ArrowRight, BadgeCheck, MapPinned, ZapOff, Lock, Wallet,
} from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Reveal, MaskedLines, EASE_LUXE } from "@/components/Reveal";
import LiveStats from "@/components/LiveStats";
import EarningsCalculator from "@/components/EarningsCalculator";
import FileUpload from "@/components/FileUpload";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const flatTrigger = "rounded-none border-white/15 bg-transparent text-white focus:ring-velvet-gold";
const flatContent = "rounded-none border-white/15 bg-velvet-coal text-white max-h-72";
const inputCls = "w-full border-b border-white/15 bg-transparent py-2.5 text-sm text-white placeholder:text-white/30 focus:border-velvet-gold focus:outline-none";

const VALUE_PROPS = [
  { icon: Wallet, title: "Keep 100%", body: "Vixera charges nothing from your bookings. Zero commission, forever." },
  { icon: BadgeCheck, title: "Verified badge", body: "One-time selfie verification. Members trust verified ladies 3× more." },
  { icon: MapPinned, title: "Tour scheduling", body: "Pre-announce visits to any SA city — get bookings before you arrive." },
  { icon: Shield, title: "Discreet inbox", body: "All chat on-platform. Your personal number stays yours until you share it." },
  { icon: ZapOff, title: "Available Now toggle", body: "Flip a switch, appear in the Live-Now feed, get booked in the next hour." },
  { icon: Sparkles, title: "Featured boosts", body: "Optional — R899 puts you top of every search for 30 days." },
];

const ADVERTISER_TESTIMONIALS = [
  { name: "Nadia · Sandton", quote: "Bookings tripled in my first month. The verification badge alone changed how men message me — no more time-wasters." },
  { name: "Aisha · Cape Town", quote: "I love that I keep everything. Other sites take 20%. On Vixera I just pay when I want featured, and it always pays for itself." },
  { name: "Jasmine · Durban", quote: "The touring feature is genius. I announced my Joburg trip and had six bookings locked in before I boarded the plane." },
];

function AdvertiserSalesPage({ user, onBecomeAdvertiser }) {
  return (
    <div className="pt-24 md:pt-28" data-testid="advertiser-sales-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-velvet-rose/20 via-velvet-ink to-black" />
        <motion.div
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 1.5, ease: EASE_LUXE }}
          className="absolute -left-40 top-40 h-96 w-96 rounded-full bg-velvet-rose/25 blur-3xl"
        />
        <div className="relative mx-auto max-w-[1600px] px-6 py-20 md:px-12 md:py-32">
          <p className="mb-6 text-xs uppercase tracking-luxe text-velvet-gold">
            For companions &middot; agencies &middot; independents
          </p>
          <MaskedLines
            lines={["Earn more.", <span key="w" className="italic text-velvet-rose">Work smarter.</span>]}
            lineClassName="font-heading text-5xl font-light leading-[0.95] tracking-tight text-white sm:text-6xl md:text-8xl"
          />
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 1, ease: EASE_LUXE }}
            className="mt-10 max-w-2xl text-lg font-light leading-relaxed text-white/70 md:text-xl"
          >
            Vixera is South Africa&rsquo;s premium companionship platform. List free,
            verify once, get discovered by high-intent members — and keep every
            rand you make.
          </motion.p>

          <div className="mt-12">
            <LiveStats variant="advertiser" />
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 1 }}
            className="mt-14 flex flex-wrap items-center gap-4"
          >
            {user ? (
              <button
                onClick={onBecomeAdvertiser}
                data-testid="become-advertiser-btn"
                className="group inline-flex items-center gap-3 rounded-full bg-velvet-rose px-8 py-4 text-xs uppercase tracking-luxe text-black transition-colors hover:bg-velvet-gold"
              >
                Get listed — takes 5 minutes
                <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
              </button>
            ) : (
              <Link
                to="/register?as=advertiser"
                data-testid="advertise-signup-btn"
                className="group inline-flex items-center gap-3 rounded-full bg-velvet-rose px-8 py-4 text-xs uppercase tracking-luxe text-black transition-colors hover:bg-velvet-gold"
              >
                Sign up as advertiser
                <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
              </Link>
            )}
            <Link
              to="/directory"
              className="text-xs uppercase tracking-luxe text-white/50 hover:text-velvet-gold"
            >
              First — see who&rsquo;s already listed &rarr;
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Value props */}
      <section className="mx-auto max-w-[1600px] px-6 py-20 md:px-12 md:py-32">
        <Reveal>
          <p className="mb-4 text-xs uppercase tracking-luxe text-velvet-gold">Why Vixera</p>
          <h2 className="mb-16 max-w-3xl font-heading text-4xl font-light leading-tight tracking-tight text-white md:text-6xl">
            Six reasons the smartest ladies in SA <span className="italic text-velvet-rose">already list here</span>.
          </h2>
        </Reveal>
        <div className="grid grid-cols-1 gap-px bg-white/5 md:grid-cols-2 lg:grid-cols-3">
          {VALUE_PROPS.map((v, i) => {
            const Icon = v.icon;
            return (
              <Reveal key={v.title} delay={i * 0.06}>
                <div className="group h-full bg-velvet-ink p-8 transition-colors hover:bg-velvet-coal">
                  <Icon size={22} className="text-velvet-gold transition-colors group-hover:text-velvet-rose" />
                  <h3 className="mt-6 font-heading text-2xl font-light text-white">{v.title}</h3>
                  <p className="mt-3 text-sm font-light leading-relaxed text-white/60">{v.body}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* Earnings calculator */}
      <section className="mx-auto max-w-[1600px] px-6 pb-20 md:px-12 md:pb-32">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-2 lg:gap-16">
          <div>
            <Reveal>
              <p className="mb-4 text-xs uppercase tracking-luxe text-velvet-gold">Your income · projected</p>
              <h2 className="font-heading text-4xl font-light leading-tight tracking-tight text-white md:text-6xl">
                Do the <span className="italic text-velvet-rose">math</span>.<br />
                See what a month on Vixera looks like.
              </h2>
              <p className="mt-8 text-base font-light leading-relaxed text-white/60">
                We take zero commission on your bookings. The only optional cost is
                the R899 featured slot — a spot at the top of every search page.
                Almost every advertiser earns that back on their first booking.
              </p>
              <div className="mt-10 flex items-center gap-4">
                <div className="h-px flex-1 bg-white/10" />
                <p className="text-[10px] uppercase tracking-luxe text-white/40">Simple maths, real bookings</p>
                <div className="h-px flex-1 bg-white/10" />
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.2}>
            <EarningsCalculator />
          </Reveal>
        </div>
      </section>

      {/* Scarcity + testimonials */}
      <section className="bg-velvet-coal/60 py-20 md:py-32">
        <div className="mx-auto max-w-[1600px] px-6 md:px-12">
          <div className="grid grid-cols-1 gap-16 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <Reveal>
                <p className="mb-4 text-xs uppercase tracking-luxe text-velvet-rose">Featured spots</p>
                <h2 className="font-heading text-4xl font-light leading-tight tracking-tight text-white md:text-5xl">
                  Only <span className="italic text-velvet-gold"><FeaturedRemaining /></span> featured slots left this month.
                </h2>
                <p className="mt-6 text-sm font-light leading-relaxed text-white/60">
                  We cap featured slots at 12 per month so members see fresh names first.
                  Once they&rsquo;re gone, the next batch opens on the 1st.
                </p>
                {user ? (
                  <button
                    onClick={onBecomeAdvertiser}
                    className="mt-10 inline-flex items-center gap-3 rounded-full bg-velvet-gold px-8 py-4 text-xs uppercase tracking-luxe text-black transition-colors hover:bg-white"
                  >
                    Claim my slot <ArrowRight size={14} />
                  </button>
                ) : (
                  <Link
                    to="/register?as=advertiser"
                    className="mt-10 inline-flex items-center gap-3 rounded-full bg-velvet-gold px-8 py-4 text-xs uppercase tracking-luxe text-black transition-colors hover:bg-white"
                  >
                    Claim my slot <ArrowRight size={14} />
                  </Link>
                )}
              </Reveal>
            </div>
            <div className="lg:col-span-7">
              <Reveal delay={0.15}>
                <p className="mb-4 text-xs uppercase tracking-luxe text-velvet-gold">In their words</p>
                <div className="space-y-6">
                  {ADVERTISER_TESTIMONIALS.map((t) => (
                    <blockquote
                      key={t.name}
                      data-testid={`testimonial-${t.name.split(" ")[0].toLowerCase()}`}
                      className="border-l-2 border-velvet-gold pl-6"
                    >
                      <p className="font-heading text-xl font-light leading-snug text-white md:text-2xl">
                        &ldquo;{t.quote}&rdquo;
                      </p>
                      <cite className="mt-3 block text-[10px] uppercase not-italic tracking-luxe text-velvet-gold">
                        — {t.name}
                      </cite>
                    </blockquote>
                  ))}
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="mx-auto max-w-[1600px] px-6 py-24 md:px-12 md:py-40 text-center">
        <Reveal>
          <p className="mb-6 text-xs uppercase tracking-luxe text-velvet-gold">Ready?</p>
          <h2 className="mx-auto max-w-4xl font-heading text-4xl font-light leading-tight text-white md:text-7xl">
            List free. <span className="italic text-velvet-rose">Get discovered.</span> Keep every rand.
          </h2>
          {user ? (
            <button
              onClick={onBecomeAdvertiser}
              data-testid="advertise-final-cta"
              className="mt-12 inline-flex items-center gap-3 rounded-full bg-velvet-rose px-10 py-5 text-xs uppercase tracking-luxe text-black transition-colors hover:bg-velvet-gold"
            >
              Become an advertiser <ArrowRight size={16} />
            </button>
          ) : (
            <Link
              to="/register?as=advertiser"
              data-testid="advertise-final-cta"
              className="mt-12 inline-flex items-center gap-3 rounded-full bg-velvet-rose px-10 py-5 text-xs uppercase tracking-luxe text-black transition-colors hover:bg-velvet-gold"
            >
              Sign up as advertiser <ArrowRight size={16} />
            </Link>
          )}
          <p className="mt-6 text-[10px] uppercase tracking-luxe text-white/30">
            Takes 5 minutes · Free forever · Verify anytime
          </p>
        </Reveal>
      </section>
    </div>
  );
}

function FeaturedRemaining() {
  const [n, setN] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    api.get("/stats/public")
      .then((r) => setN(r.data.featured_spots_remaining))
      .catch(() => setErr(true));
  }, []);
  const label = err ? "<12" : n ?? "…";
  return <span data-testid="featured-remaining" className="tabular-nums">{label}</span>;
}


export default function Advertise() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [cities, setCities] = useState([]);
  const [cats, setCats] = useState([]);
  const [races, setRaces] = useState([]);
  const [genders, setGenders] = useState([]);
  const [saving, setSaving] = useState(false);
  const [hero, setHero] = useState(null);
  const [gallery, setGallery] = useState([]);
  const [videos, setVideos] = useState([]);
  const [extras, setExtras] = useState([]);  // [{name, price, note}]
  const [form, setForm] = useState({
    name: "", city: "", town: "", age: 21, category: "Ladies", race: "", gender: "Female",
    height: "", tagline: "", bio: "", languages: "English", interests: "",
    whatsapp: "", phone: "", telegram: "",
    incall: true, outcall: false,
  });
  const [categories, setCategories] = useState([]);  // multi-select

  const toggleCat = (c) => {
    setCategories((prev) =>
      prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]);
  };

  useEffect(() => {
    // Non-authed users see the sales page — do NOT redirect to /login.
  }, [user]);

  const isAdvertiser = user && (user.account_type === "advertiser" || user.role === "admin");

  const becomeAdvertiser = async () => {
    try {
      await api.post("/auth/account-type", { account_type: "advertiser" });
      toast.success("You're now an Advertiser — you can list your ad below.");
      window.location.reload();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  useEffect(() => {
    api.get("/locations").then((r) => setCities(r.data.flatMap((p) => p.cities))).catch(() => {});
    api.get("/categories").then((r) => setCats(r.data.map((c) => c.name))).catch(() => {});
    api.get("/races").then((r) => setRaces(r.data)).catch(() => {});
    api.get("/genders").then((r) => setGenders(r.data)).catch(() => {});
  }, []);

  const towns = cities.find((c) => c.slug === form.city)?.towns || [];

  const submit = async (e) => {
    e.preventDefault();
    if (!form.city || !hero?.url) { toast.error("City and a main photo are required"); return; }
    if (categories.length === 0) { toast.error("Pick at least one category"); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name, city: form.city, town: form.town, age: Number(form.age),
        category: categories[0],           // legacy singular
        categories,                        // full multi list
        race: form.race, gender: form.gender, height: form.height,
        tagline: form.tagline, bio: form.bio,
        languages: form.languages.split(",").map((s) => s.trim()).filter(Boolean),
        interests: form.interests.split(",").map((s) => s.trim()).filter(Boolean),
        hero_image: hero.url,
        gallery: [...gallery.map((g) => g.url), ...videos.map((v) => v.url)],
        contact: { whatsapp: form.whatsapp, phone: form.phone, telegram: form.telegram },
        incall: form.incall, outcall: form.outcall,
      };
      const { data } = await api.post("/listings", payload);
      toast.success("Your listing is live!");
      navigate(`/companion/${data.slug}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (!isAdvertiser) {
    return <AdvertiserSalesPage user={user} onBecomeAdvertiser={becomeAdvertiser} />;
  }

  return (
    <div className="pt-28 md:pt-36" data-testid="advertise-page">
      <div className="mx-auto max-w-4xl px-6 md:px-12">
        <Reveal>
          <p className="mb-4 text-xs uppercase tracking-luxe text-velvet-gold">Advertise with Vixera</p>
          <h1 className="font-heading text-5xl font-semibold leading-none tracking-tight text-white md:text-6xl">
            Create your <span className="italic text-velvet-rose">listing</span>
          </h1>
          <p className="mt-6 text-sm font-light text-white/50">Free to list. Boost to feature. You control your profile anytime from your account.</p>
        </Reveal>

        <form onSubmit={submit} className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-2" data-testid="advertise-form">
          <input required placeholder="Display name" value={form.name} data-testid="adv-name" onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
          <input type="number" min="18" max="99" placeholder="Age" value={form.age} data-testid="adv-age" onChange={(e) => setForm({ ...form, age: e.target.value })} className={inputCls} />

          <div>
            <label className="mb-2 block text-[10px] uppercase tracking-luxe text-white/40">Categories (pick one or more)</label>
            <div className="flex flex-wrap gap-2" data-testid="adv-categories">
              {cats.map((c) => {
                const active = categories.includes(c);
                return (
                  <button
                    key={c}
                    type="button"
                    onClick={() => toggleCat(c)}
                    data-testid={`adv-cat-${c.toLowerCase().replace(/\s+/g, "-")}`}
                    className={`rounded-full border px-4 py-2 text-[11px] uppercase tracking-luxe transition-colors ${
                      active
                        ? "border-velvet-gold bg-velvet-gold text-black"
                        : "border-white/15 text-white/60 hover:border-velvet-gold hover:text-velvet-gold"
                    }`}
                  >
                    {c}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <label className="mb-2 block text-[10px] uppercase tracking-luxe text-white/40">Gender</label>
            <Select value={form.gender} onValueChange={(v) => setForm({ ...form, gender: v })}>
              <SelectTrigger className={flatTrigger} data-testid="adv-gender"><SelectValue /></SelectTrigger>
              <SelectContent className={flatContent}>{genders.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <label className="mb-2 block text-[10px] uppercase tracking-luxe text-white/40">Race / Ethnicity</label>
            <Select value={form.race} onValueChange={(v) => setForm({ ...form, race: v })}>
              <SelectTrigger className={flatTrigger} data-testid="adv-race"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent className={flatContent}>{races.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          <div>
            <label className="mb-2 block text-[10px] uppercase tracking-luxe text-white/40">City</label>
            <Select value={form.city} onValueChange={(v) => setForm({ ...form, city: v, town: "" })}>
              <SelectTrigger className={flatTrigger} data-testid="adv-city"><SelectValue placeholder="Select city" /></SelectTrigger>
              <SelectContent className={flatContent}>{cities.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <label className="mb-2 block text-[10px] uppercase tracking-luxe text-white/40">Town / Area</label>
            <Select value={form.town || "none"} onValueChange={(v) => setForm({ ...form, town: v === "none" ? "" : v })}>
              <SelectTrigger className={flatTrigger} data-testid="adv-town"><SelectValue placeholder="Optional" /></SelectTrigger>
              <SelectContent className={flatContent}><SelectItem value="none">— Any —</SelectItem>{towns.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          <input placeholder="Tagline" value={form.tagline} data-testid="adv-tagline" onChange={(e) => setForm({ ...form, tagline: e.target.value })} className={`${inputCls} md:col-span-2`} />
          <textarea placeholder="About you" rows={3} value={form.bio} data-testid="adv-bio" onChange={(e) => setForm({ ...form, bio: e.target.value })} className={`${inputCls} md:col-span-2`} />

          <input placeholder="Languages (comma separated)" value={form.languages} onChange={(e) => setForm({ ...form, languages: e.target.value })} className={inputCls} />
          <input placeholder="Interests (comma separated)" value={form.interests} onChange={(e) => setForm({ ...form, interests: e.target.value })} className={inputCls} />

          <input placeholder="WhatsApp number" value={form.whatsapp} data-testid="adv-whatsapp" onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} className={inputCls} />
          <input placeholder="Phone number" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} />

          <div className="md:col-span-2">
            <FileUpload kind="photo" single value={hero} onChange={setHero} label="Main photo (required)" />
          </div>

          <div className="md:col-span-2">
            <FileUpload kind="photo" value={gallery} onChange={setGallery} max={8} label="Gallery (up to 8 photos)" />
          </div>

          <div className="md:col-span-2">
            <FileUpload kind="video" value={videos} onChange={setVideos} max={2} label="Videos (up to 2, 60MB each) — Premium showcase" />
          </div>

          <div className="md:col-span-2" data-testid="adv-extras-section">
            <div className="mb-3 flex items-center justify-between">
              <label className="block text-[10px] uppercase tracking-luxe text-white/40">Extras not included</label>
              <button type="button" data-testid="adv-extras-add"
                onClick={() => setExtras([...extras, { name: "", price: "", note: "" }])}
                className="text-[10px] uppercase tracking-luxe text-velvet-gold hover:text-white">
                + Add extra
              </button>
            </div>
            {extras.length === 0 && (
              <p className="text-xs font-light text-white/40">
                List any services or items that are NOT included in your base rate — for example: dinner date, overnight, lingerie, prostate play, video calls, custom content. Each is quoted separately.
              </p>
            )}
            <div className="space-y-2">
              {extras.map((ex, i) => (
                <div key={i} className="grid grid-cols-1 gap-2 border border-white/10 p-3 md:grid-cols-[1fr_120px_1fr_auto]" data-testid={`adv-extras-row-${i}`}>
                  <input placeholder="Name (e.g. Dinner date)" value={ex.name}
                    onChange={(e) => setExtras(extras.map((x, j) => j === i ? { ...x, name: e.target.value } : x))}
                    className={inputCls} />
                  <input placeholder="R price" value={ex.price}
                    onChange={(e) => setExtras(extras.map((x, j) => j === i ? { ...x, price: e.target.value } : x))}
                    className={inputCls} />
                  <input placeholder="Note (optional)" value={ex.note}
                    onChange={(e) => setExtras(extras.map((x, j) => j === i ? { ...x, note: e.target.value } : x))}
                    className={inputCls} />
                  <button type="button"
                    onClick={() => setExtras(extras.filter((_, j) => j !== i))}
                    className="text-white/40 hover:text-velvet-rose">✕</button>
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-8 md:col-span-2">
            <label className="flex items-center gap-2 text-xs uppercase tracking-luxe text-white/60"><input type="checkbox" checked={form.incall} onChange={(e) => setForm({ ...form, incall: e.target.checked })} /> Incall</label>
            <label className="flex items-center gap-2 text-xs uppercase tracking-luxe text-white/60"><input type="checkbox" checked={form.outcall} onChange={(e) => setForm({ ...form, outcall: e.target.checked })} /> Outcall</label>
          </div>

          <button type="submit" disabled={saving} data-testid="adv-submit" className="rounded-full bg-white py-4 text-xs uppercase tracking-luxe text-black transition-colors hover:bg-velvet-rose hover:text-white disabled:opacity-50 md:col-span-2">
            {saving ? "Publishing…" : "Publish my listing"}
          </button>
        </form>
      </div>
    </div>
  );
}
